import { unwrapAssetEnvelope } from '../api/getAssetById';
import { normalizeBookingCreateAddons, type BookingCreateAddonWire } from '../api/bookingCreatePayload';
import {
  buildBookingAddonsWirePayload,
  type AssetBookingAddonOption,
} from './bookingAssetAddons';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function coerceBookingIdScalar(raw: unknown): string | null {
  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim();
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return String(raw);
  }
  return null;
}

/** Primary booking id from a list or detail record. */
export function bookingRecordPrimaryId(booking: Record<string, unknown>): string | null {
  const raw =
    booking.id ??
    booking.Id ??
    booking.booking_id ??
    booking.bookingId ??
    booking.BookingId ??
    booking.uuid ??
    booking.UUID ??
    booking.booking_uuid ??
    booking.bookingUuid;
  return coerceBookingIdScalar(raw);
}

function unwrapBookingEnvelopeOnce(o: Record<string, unknown>): unknown {
  const keys = Object.keys(o).map((k) => k.toLowerCase());
  if (keys.length === 1 && keys[0] === 'booking' && isPlainObject(o.booking)) {
    return o.booking;
  }
  return unwrapAssetEnvelope(o);
}

/** Booking id from POST /bookings/create (flat, PascalCase, or wrapped payloads). */
export function extractBookingIdFromApiPayload(raw: unknown): string | null {
  const direct = coerceBookingIdScalar(raw);
  if (direct) {
    return direct;
  }
  let cur: unknown = raw;
  for (let depth = 0; depth < 6; depth++) {
    if (!isPlainObject(cur)) {
      break;
    }
    const id = bookingRecordPrimaryId(cur);
    if (id) {
      return id;
    }
    const next = unwrapBookingEnvelopeOnce(cur);
    if (next === cur) {
      break;
    }
    cur = next;
  }
  return null;
}

function bookingRecordAssetId(booking: Record<string, unknown>): string {
  const raw = booking.asset_id ?? booking.assetId ?? booking.AssetId;
  return typeof raw === 'string' ? raw.trim() : '';
}

function bookingRecordCreatedMs(booking: Record<string, unknown>): number {
  const raw =
    booking.booking_date ??
    booking.created_at ??
    booking.createdAt ??
    booking.created ??
    booking.start_date;
  if (typeof raw === 'string') {
    const ms = Date.parse(raw);
    return Number.isNaN(ms) ? -Infinity : ms;
  }
  return -Infinity;
}

/**
 * Recover the id of a just-created booking from the refreshed `GET /bookings` list when the
 * `POST /bookings/create` response did not echo an id we can read. Prefers an exact asset +
 * schedule match, then falls back to the newest booking for the asset.
 */
export function recoverCreatedBookingId(
  records: Record<string, unknown>[],
  match: { assetId?: string | null; startIso?: string | null; endIso?: string | null },
): string | null {
  const assetId = (match.assetId ?? '').trim();
  const startMs = match.startIso ? Date.parse(match.startIso) : NaN;
  const endMs = match.endIso ? Date.parse(match.endIso) : NaN;

  const candidates = records.filter((r) => {
    if (!bookingRecordPrimaryId(r)) {
      return false;
    }
    if (assetId) {
      const rid = bookingRecordAssetId(r);
      if (rid && rid !== assetId) {
        return false;
      }
    }
    return true;
  });

  if (!Number.isNaN(startMs) && !Number.isNaN(endMs)) {
    const exact = candidates.find((r) => {
      const s = typeof r.start_date === 'string' ? Date.parse(r.start_date) : NaN;
      const e = typeof r.end_date === 'string' ? Date.parse(r.end_date) : NaN;
      return s === startMs && e === endMs;
    });
    if (exact) {
      return bookingRecordPrimaryId(exact);
    }
  }

  let best: Record<string, unknown> | null = null;
  let bestMs = -Infinity;
  for (const r of candidates) {
    const ms = bookingRecordCreatedMs(r);
    if (ms >= bestMs) {
      bestMs = ms;
      best = r;
    }
  }
  return best ? bookingRecordPrimaryId(best) : null;
}

function bookingStatusNameLower(booking: Record<string, unknown>): string {
  if (typeof booking.status_name === 'string' && booking.status_name.trim()) {
    return booking.status_name.trim().toLowerCase();
  }
  if (typeof booking.status === 'string' && booking.status.trim()) {
    return booking.status.trim().toLowerCase();
  }
  return '';
}

/** Cancelled or completed — kept by GET /bookings but hidden from My Bookings. */
export function bookingRecordIsTerminal(booking: Record<string, unknown>): boolean {
  if (typeof booking.cancelled_at === 'string' && booking.cancelled_at.trim()) {
    return true;
  }
  const statusId = booking.status_id;
  if (statusId === 4) {
    return true;
  }
  if (typeof statusId === 'string' && statusId.trim() === '4') {
    return true;
  }
  const statusName = bookingStatusNameLower(booking);
  if (statusName.includes('complet') || statusName.includes('cancel')) {
    return true;
  }
  return false;
}

/** Active rows only — excludes cancelled/completed bookings the API still returns. */
export function filterActiveMyBookingsListRecords(
  records: Record<string, unknown>[],
): Record<string, unknown>[] {
  return records.filter((booking) => !bookingRecordIsTerminal(booking));
}

export type BookingListTimeFilter = 'currentAndFuture' | 'past';

/** Parsed `end_date` in epoch ms, or `null` when missing or invalid. */
export function bookingRecordEndMs(booking: Record<string, unknown>): number | null {
  const raw = booking.end_date ?? booking.endDate;
  if (typeof raw === 'string' && raw.trim()) {
    const ms = Date.parse(raw.trim());
    return Number.isNaN(ms) ? null : ms;
  }
  return null;
}

/** Parsed `start_date` in epoch ms, or `null` when missing or invalid. */
export function bookingRecordStartMs(booking: Record<string, unknown>): number | null {
  const raw = booking.start_date ?? booking.startDate;
  if (typeof raw === 'string' && raw.trim()) {
    const ms = Date.parse(raw.trim());
    return Number.isNaN(ms) ? null : ms;
  }
  return null;
}

export const UPCOMING_BOOKING_NOTIFICATION_WINDOW_MS = 24 * 60 * 60 * 1000;

/** Active booking whose start is between `nowMs` and `nowMs + windowMs` (inclusive). */
export function bookingRecordStartsWithinWindow(
  booking: Record<string, unknown>,
  windowMs: number,
  nowMs: number = Date.now(),
): boolean {
  if (bookingRecordIsTerminal(booking)) {
    return false;
  }
  const startMs = bookingRecordStartMs(booking);
  if (startMs === null) {
    return false;
  }
  return startMs >= nowMs && startMs <= nowMs + windowMs;
}

/** Active bookings starting within the window, soonest first. */
export function filterBookingsStartingWithinWindow(
  records: Record<string, unknown>[],
  windowMs: number = UPCOMING_BOOKING_NOTIFICATION_WINDOW_MS,
  nowMs: number = Date.now(),
): Record<string, unknown>[] {
  return records
    .filter((booking) => bookingRecordStartsWithinWindow(booking, windowMs, nowMs))
    .sort((a, b) => (bookingRecordStartMs(a) ?? 0) - (bookingRecordStartMs(b) ?? 0));
}

/** True when the booking’s end time is strictly before `nowMs`. */
export function bookingRecordIsPastByEndDate(
  booking: Record<string, unknown>,
  nowMs: number = Date.now(),
): boolean {
  const endMs = bookingRecordEndMs(booking);
  if (endMs === null) {
    return false;
  }
  return endMs < nowMs;
}

/** Upcoming and in-progress bookings (`end_date` ≥ now, or no parseable end). */
export function bookingRecordIsCurrentOrFutureByEndDate(
  booking: Record<string, unknown>,
  nowMs: number = Date.now(),
): boolean {
  return !bookingRecordIsPastByEndDate(booking, nowMs);
}

export function filterMyBookingsByListTimeFilter(
  records: Record<string, unknown>[],
  filter: BookingListTimeFilter,
  nowMs: number = Date.now(),
): Record<string, unknown>[] {
  if (filter === 'past') {
    return records.filter((booking) => bookingRecordIsPastByEndDate(booking, nowMs));
  }
  return records.filter((booking) => bookingRecordIsCurrentOrFutureByEndDate(booking, nowMs));
}

export type BookingMutateBlockReason = 'none' | 'no_id' | 'terminal' | 'past';

/** Why cancel/update is blocked — `none` when {@link bookingRecordCanMutate} is true. */
export function bookingRecordMutateBlockReason(
  booking: Record<string, unknown>,
  nowMs: number = Date.now(),
): BookingMutateBlockReason {
  if (!bookingRecordPrimaryId(booking)) {
    return 'no_id';
  }
  if (bookingRecordIsTerminal(booking)) {
    return 'terminal';
  }
  if (bookingRecordIsPastByEndDate(booking, nowMs)) {
    return 'past';
  }
  return 'none';
}

/** Whether the signed-in client can still cancel or edit this booking from the list. */
export function bookingRecordCanMutate(
  booking: Record<string, unknown>,
  nowMs: number = Date.now(),
): boolean {
  return bookingRecordMutateBlockReason(booking, nowMs) === 'none';
}

/** API expects a whole number ≥ 1 (class-validator `@IsInt()` on PATCH `/bookings/:id`). */
export function coerceBookingWireTotalPrice(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const n = Math.round(raw);
    return n >= 1 ? n : null;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Math.round(Number(raw.trim().replace(',', '.')));
    return Number.isFinite(n) && n >= 1 ? n : null;
  }
  return null;
}

/** Map a booking GET/list row into the create/update form body. */
export function bookingUpdateBodyFromRecord(record: Record<string, unknown>): Record<string, unknown> {
  const addons = Array.isArray(record.addons)
    ? record.addons.filter(isPlainObject).map((row) => ({ ...row }))
    : [];
  const totalPrice = coerceBookingWireTotalPrice(record.total_price);
  const pricingTypeId =
    typeof record.pricing_type_id === 'number' && Number.isFinite(record.pricing_type_id)
      ? record.pricing_type_id
      : typeof record.pricing_type_id === 'string' && record.pricing_type_id.trim()
        ? Number(record.pricing_type_id.trim())
        : null;
  return {
    asset_id:
      typeof record.asset_id === 'string'
        ? record.asset_id.trim()
        : typeof record.asset_id === 'number' && Number.isFinite(record.asset_id)
          ? String(record.asset_id)
          : '',
    amount_of_people: record.amount_of_people ?? null,
    start_date: typeof record.start_date === 'string' ? record.start_date : '',
    end_date: typeof record.end_date === 'string' ? record.end_date : '',
    total_price: totalPrice,
    pricing_type_id:
      pricingTypeId !== null && Number.isFinite(pricingTypeId) ? pricingTypeId : null,
    addons,
  };
}

export type NormalizeBookingUpdateOptions = {
  /** When set, `end_date` is sent only if it is strictly after this value (extend-only). */
  originalEndDate?: string;
  /** When set, rebuild/clamp `addons` from form body using edit-aware caps. */
  addonChoices?: AssetBookingAddonOption[];
  /** Quantities on the booking when edit started (for incremental PATCH add-ons). */
  reservedAddonQty?: ReadonlyMap<number, number>;
};

/**
 * PATCH add-ons the API should apply when it merges partial addon updates:
 * only new add-ons or lines whose quantity increased vs when edit started.
 * (Omit unchanged lines so the server does not re-check pool for existing allocation.)
 */
export function filterIncrementalBookingPatchAddons(
  wireAddons: BookingCreateAddonWire[],
  reservedOnBooking: ReadonlyMap<number, number>,
): BookingCreateAddonWire[] {
  if (reservedOnBooking.size === 0) {
    return wireAddons;
  }
  return wireAddons.filter((row) => {
    const prev = reservedOnBooking.get(row.addon_id) ?? 0;
    return prev === 0 || row.quantity > prev;
  });
}

function bookingEndIsoMillis(iso: string): number | null {
  const t = iso.trim();
  if (!t) {
    return null;
  }
  const ms = new Date(t).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** PATCH `/bookings/:id` — `addons` and integer `total_price`; `end_date` only when extending. */
export function normalizeBookingUpdatePayload(
  body: Record<string, unknown>,
  options?: NormalizeBookingUpdateOptions,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const endRaw = typeof body.end_date === 'string' ? body.end_date.trim() : '';
  const originalEnd = options?.originalEndDate?.trim() ?? '';
  if (endRaw) {
    const endMs = bookingEndIsoMillis(endRaw);
    const originalMs = bookingEndIsoMillis(originalEnd);
    const isExtension =
      originalMs === null || endMs === null || endMs > originalMs;
    if (isExtension) {
      out.end_date = endRaw;
    }
  }
  const totalPrice = coerceBookingWireTotalPrice(body.total_price);
  if (totalPrice !== null) {
    out.total_price = totalPrice;
  }
  if ('addons' in body) {
    const wire = options?.addonChoices
      ? buildBookingAddonsWirePayload(options.addonChoices, body.addons)
      : normalizeBookingCreateAddons(body.addons);
    const reserved = options?.reservedAddonQty;
    out.addons =
      reserved && reserved.size > 0
        ? filterIncrementalBookingPatchAddons(wire, reserved)
        : wire;
  }
  return out;
}
